"use client";

import { useState } from "react";
import { Download } from "lucide-react";

type Phase = "form" | "code" | "ready";

export function GatedLeadMagnetDownload({
  magnetSlug,
  title,
  description,
  label,
}: {
  magnetSlug: string;
  title: string;
  description: string;
  label: string;
}) {
  const [phase, setPhase] = useState<Phase>("form");
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [code, setCode] = useState("");
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/lead-magnets/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ magnetSlug, email, businessName }),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(typeof json.error === "string" ? json.error : "Could not send the code.");
        return;
      }
      setPhase("code");
    } catch {
      setError("Could not send the code. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/lead-magnets/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ magnetSlug, email, code: code.trim() }),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string; downloadUrl?: string };
      if (!res.ok || typeof json.downloadUrl !== "string") {
        setError(typeof json.error === "string" ? json.error : "Could not verify that code.");
        return;
      }
      setDownloadUrl(json.downloadUrl);
      setPhase("ready");
    } catch {
      setError("Could not verify that code. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const fieldClass =
    "mt-1.5 w-full border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-950 outline-none focus:border-[#0d1726] focus:ring-1 focus:ring-[#0d1726]";

  return (
    <aside className="border border-slate-200 bg-white px-5 py-5 sm:px-6">
      <p className="font-[family-name:var(--landing-display)] text-lg font-semibold text-slate-950">
        {title}
      </p>
      <p className="mt-2 text-[0.98rem] leading-7 text-slate-600">{description}</p>

      {phase === "form" ? (
        <form onSubmit={requestCode} className="mt-5 space-y-3">
          <div>
            <label htmlFor={`lm-business-${magnetSlug}`} className="block text-sm font-medium text-slate-800">
              Business name
            </label>
            <input
              id={`lm-business-${magnetSlug}`}
              name="businessName"
              autoComplete="organization"
              required
              minLength={2}
              maxLength={200}
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className={fieldClass}
            />
          </div>
          <div>
            <label htmlFor={`lm-email-${magnetSlug}`} className="block text-sm font-medium text-slate-800">
              Email
            </label>
            <input
              id={`lm-email-${magnetSlug}`}
              name="email"
              type="email"
              autoComplete="email"
              required
              maxLength={320}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={fieldClass}
            />
          </div>
          <p className="text-xs leading-5 text-slate-500">
            We send a 6-digit code first. The file unlocks after that code checks out.
          </p>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex min-h-11 w-full items-center justify-center bg-[#0d1726] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {busy ? "Sending code..." : "Email me a verification code"}
          </button>
        </form>
      ) : null}

      {phase === "code" ? (
        <form onSubmit={verifyCode} className="mt-5 space-y-3">
          <p className="text-sm leading-6 text-slate-600">
            Enter the 6-digit code we sent to <span className="font-medium text-slate-900">{email.trim()}</span>.
          </p>
          <div>
            <label htmlFor={`lm-code-${magnetSlug}`} className="block text-sm font-medium text-slate-800">
              Verification code
            </label>
            <input
              id={`lm-code-${magnetSlug}`}
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className={`${fieldClass} tracking-[0.2em] sm:max-w-[12rem]`}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="inline-flex min-h-11 items-center justify-center bg-[#0d1726] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Checking..." : "Verify and unlock download"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPhase("form");
                setCode("");
                setError(null);
              }}
              className="inline-flex min-h-11 items-center justify-center border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-60"
            >
              Edit email
            </button>
          </div>
        </form>
      ) : null}

      {phase === "ready" && downloadUrl ? (
        <div className="mt-5 space-y-3">
          <p className="text-sm leading-6 text-slate-600">Email verified. Your worksheet is ready.</p>
          <a
            href={downloadUrl}
            className="inline-flex min-h-11 items-center justify-center gap-2 bg-[#0d1726] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0d1726]"
          >
            <Download className="h-4 w-4" aria-hidden />
            {label}
          </a>
        </div>
      ) : null}

      {error ? <p className="mt-3 text-sm font-medium text-red-700">{error}</p> : null}
    </aside>
  );
}
